import { Feature, Polygon, point, booleanPointInPolygon, bbox } from '@turf/turf';
import mapboxgl from 'mapbox-gl';

export function snapToStep(value: number, step: number): number {
  return Math.floor(value / step) * step;
}

export function getPointsInPolygon(polygon: Feature<Polygon>, resolution: number) {
  try {
    const [minLon, minLat, maxLon, maxLat] = bbox(polygon);

    const snappedMinLon = snapToStep(minLon, resolution);
    const snappedMinLat = snapToStep(minLat, resolution);
    const snappedMaxLon = snapToStep(maxLon, resolution) + resolution;
    const snappedMaxLat = snapToStep(maxLat, resolution) + resolution;

    const points = [];

    for (let lat = snappedMaxLat; lat >= snappedMinLat; lat -= resolution) {
      for (let lon = snappedMinLon; lon <= snappedMaxLon; lon += resolution) {
        const cellCenterLat = lat - resolution/2;
        const cellCenterLon = lon + resolution/2;
        
        if (booleanPointInPolygon(point([cellCenterLon, cellCenterLat]), polygon)) {
          points.push({ lat: cellCenterLat, lon: cellCenterLon });
        }
      }
    }

    return points;
  } catch (error) {
    console.error('Error generating points in polygon:', error);
    throw error;
  }
}

export function zoomToPolygon(map: mapboxgl.Map, polygon: GeoJSON.Feature) {
  try {
    const [minLon, minLat, maxLon, maxLat] = bbox(polygon);
    
    map.fitBounds(
      [[minLon, minLat], [maxLon, maxLat]],
      {
        padding: 50,
        duration: 1000,
        maxZoom: 15 // Add zoom limit
      }
    );
  } catch (error) {
    console.error('Error zooming to polygon:', error);
  }
}

export function getPolygonColor(index: number): string {
  const colors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFA500', // Orange
    '#800080', // Purple
    '#00FFFF', // Cyan
    '#FF00FF', // Magenta
    '#FFFF00'  // Yellow
  ];
  return colors[index % colors.length];
}

export function initializeMapBounds(map: mapboxgl.Map) {
  // Set initial bounds to continental US with extra padding
  map.fitBounds([
    [-130.0, 22.0], // Southwest coordinates - expanded
    [-60.0, 52.0]   // Northeast coordinates - expanded
  ], {
    padding: { top: 100, bottom: 100, left: 100, right: 100 }, // Increased padding
    duration: 0, // Instant
    maxZoom: 3  // Reduced max zoom for a more zoomed-out view
  });

  // Set zoom constraints
  map.setMinZoom(2); // Allow zooming out further
  map.setMaxZoom(15);
}